import React from "react";
import { useParams, useLocation } from "react-router-dom";
import { lazyWithRetry } from "../utils/lazyWithRetry";
import { useDocumentTypesContext } from "../context/DocumentTypesContext";
import RNWorkOrder from "../screens/RNWorkOrder";
import DocumentsManufacturing from "../screens/DocumentsManufacturing";

/**
 * Pattern -> screen registry (routing decision).
 *
 * The document type's `pattern` (set in /documents, DocumentPatternSelect —
 * vocab fetched from the server, never a static client list) is the ONE
 * thing that decides which screen renders for a document instance. Every
 * pattern currently in use (server `constants/documentPatterns.js`) has an
 * explicit entry here, so there is no silent URL-based fallback once a
 * pattern is set. A document type with NO pattern (prod may still have
 * some) falls through to the calling route's own `fallback` screen — see
 * `PatternDocumentScreen` below.
 *
 * These are the SINGLE lazy() declarations for every screen listed here —
 * do not add a second lazy() of the same module elsewhere (routeConfig.tsx
 * imports the fallback screens it needs from this file).
 *
 * RNWorkOrder/DocumentsManufacturing are imported eagerly (not lazy), same
 * as the WorkOrderRenderer screen this module replaces — they were never
 * lazy-loaded in routeConfig.tsx to begin with.
 */
export { DocumentsManufacturing };
export const GenericDocumentScreen = lazyWithRetry(() => import("../screens/GenericDocumentScreen"));
export const DocumentsMaterialScreen = lazyWithRetry(() => import("../screens/DocumentsMaterial"));
export const DocumentsNonMaterialScreen = lazyWithRetry(() => import("../screens/DocumentsNonMaterial"));
export const CommercialContactsScreen = lazyWithRetry(() => import("../screens/CommercialContacts"));
export const DocumentsIndirectBuyerScreen = lazyWithRetry(() => import("../screens/DocumentsIndirectBuyer"));
export const SettingSellingPricesNonMaterialScreen = lazyWithRetry(() => import("../screens/SettingSellingPricesNonMaterial"));
export const ReservedContactsScreen = lazyWithRetry(() => import("../screens/ReservedContacts"));
export const DocumentsTransferScreen = lazyWithRetry(() => import("../screens/DocumentsTransfer"));
export const DocumentsInventoryCountScreen = lazyWithRetry(() => import("../screens/DocumentsInventoryCount"));
export const DocumentsInventoryAdjustmentScreen = lazyWithRetry(() => import("../screens/DocumentsInventoryAdjustment"));
export const DocumentsInvoiceScreen = lazyWithRetry(() => import("../screens/DocumentsInvoice"));
export const DocumentsPlanScreen = lazyWithRetry(() => import("../screens/DocumentsPlan"));
export const DocumentsPlan2Screen = lazyWithRetry(() => import("../screens/DocumentsPlan2"));
export const DocumentsPackingListScreen = lazyWithRetry(() => import("../screens/DocumentsPackingList"));

/** RN-PROST is the RN screen in its simplified variant — same component, one prop. */
const RNWorkOrderSimple: React.FC = () => <RNWorkOrder variant="simple" />;

/**
 * pattern -> screen. Keys mirror server `constants/documentPatterns.js`
 * PATTERNS exactly (incl. the 3 new ones: INDIREKTEN-KUPUVAC,
 * PRODAZNI-CENI, REZERVIRANI-KONTAKTI). No hardcoded document codes.
 */
export const PATTERN_SCREEN_MAP: Record<string, React.ComponentType> = {
  "EXTERNI": GenericDocumentScreen,
  "INTERNI-RN": GenericDocumentScreen,
  "POCETNA-SOSTOJBA": GenericDocumentScreen,
  "RN": RNWorkOrder,
  "RN-PROST": RNWorkOrderSimple,
  "RABOTEN-NALOG": DocumentsManufacturing,
  "PRENOS": DocumentsTransferScreen,
  "INVENTAR": DocumentsInventoryCountScreen,
  "KUSOK-VISOK": DocumentsInventoryAdjustmentScreen,
  "NALOG-TRANSPORT": DocumentsPackingListScreen,
  "INTERNI": DocumentsPackingListScreen,
  "KONTAKTI": CommercialContactsScreen,
  "PONUDA": DocumentsNonMaterialScreen,
  "FAKTURA": DocumentsInvoiceScreen,
  "PLAN": DocumentsPlanScreen,
  "PLAN2": DocumentsPlan2Screen,
  "INDIREKTEN-KUPUVAC": DocumentsIndirectBuyerScreen,
  "PRODAZNI-CENI": SettingSellingPricesNonMaterialScreen,
  "REZERVIRANI-KONTAKTI": ReservedContactsScreen,
};

/** Looks up the mapped screen for a pattern, or returns `fallback` when the pattern is unset/unknown. */
export function resolveDocumentScreen(
  pattern: string | null | undefined,
  fallback: React.ComponentType
): React.ComponentType {
  if (pattern && PATTERN_SCREEN_MAP[pattern]) {
    return PATTERN_SCREEN_MAP[pattern];
  }
  return fallback;
}

interface PatternDocumentScreenProps {
  /** Route's current default screen — rendered when the doc type has no pattern, or an unknown one. */
  fallback: React.ComponentType;
  /** Which route param carries the document code. Defaults to "documentType". */
  paramName?: "documentType" | "code";
  /**
   * Precedence between the URL param and `location.state.code`. Defaults to
   * true (state wins) — matches DocumentRenderer/NonMaterialRouter. The
   * work-order route deliberately prefers the URL param (drag-to-bookmark
   * shortcuts pass the menu LABEL as state.code) — pass `false` there.
   */
  preferState?: boolean;
}

/**
 * Single dispatcher for every document route. Resolves the document type's
 * `pattern` from DocumentTypesContext and renders PATTERN_SCREEN_MAP[pattern]
 * when known, otherwise the route's own `fallback` screen — never re-derived
 * from the URL's category/subCategory/subType/menuGroup segments and never a
 * hardcoded document code.
 */
export const PatternDocumentScreen: React.FC<PatternDocumentScreenProps> = ({
  fallback,
  paramName = "documentType",
  preferState = true,
}) => {
  const params = useParams<{ documentType?: string; code?: string }>();
  const location = useLocation();
  const { documentTypes, loading } = useDocumentTypesContext();

  const paramCode = paramName === "code" ? params.code : params.documentType;
  const stateCode = (location.state as { code?: string } | null)?.code;
  const code = preferState ? (stateCode || paramCode) : (paramCode || stateCode);

  // Loading guard: DocumentTypesContext starts empty on a hard refresh and
  // fetches asynchronously. Without this guard the fallback would mount
  // first, then get replaced once documentTypes resolves — a visible
  // flicker + a wasted fallback mount/fetch cycle.
  if (loading && documentTypes.length === 0) {
    return null;
  }

  const docType = documentTypes.find((dt) => dt.code === code);
  const Screen = resolveDocumentScreen(docType?.pattern, fallback);
  return <Screen />;
};
